import React from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import DashboardWrapper from "../../components/Layout/DashboardWrapper";
import DoctorAvailabilityPanel from "../../components/Availability/DoctorAvailabilityPanel";
import { DoctorSelect } from "../../components/Queue/QueueModalParts";
import { isDoctor, routePaths } from "../../constants/roles";
import useDoctors from "../../hooks/queries/useDoctors";
import {
  AVAILABILITY_DOCTOR_PARAM,
  canChooseDoctor,
  canEditAvailability,
} from "../../utils/availability";
import { getLoggedInUser } from "../../utils/storageUtils";

/**
 * Disponibilité d'un médecin, hors de son Profil : ouverte depuis l'écran
 * Médecins (administrateur) ou par lien (secrétaire, lecture seule). Pas
 * d'entrée de menu : un médecin gère la sienne dans son Profil, il y est renvoyé.
 */
const Availability: React.FC = () => {
  const currentUser = getLoggedInUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const showDoctorChoice = canChooseDoctor(currentUser);
  const doctors = useDoctors(showDoctorChoice);

  const doctorIdFromUrl = Number(searchParams.get(AVAILABILITY_DOCTOR_PARAM)) || null;
  const doctorId = doctorIdFromUrl ?? doctors[0]?.id ?? null;
  const editable = canEditAvailability(currentUser, doctorId);
  const doctor = doctors.find((candidate) => candidate.id === doctorId);

  if (!showDoctorChoice && isDoctor(currentUser)) {
    return <Navigate to={routePaths.PROFILE} replace />;
  }

  return (
    <DashboardWrapper>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-emerald-700 font-semibold">Agenda</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Disponibilités{doctor ? ` · Dr ${doctor.firstName ?? ""} ${doctor.lastName ?? ""}`.trimEnd() : ""}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Jours et heures de consultation, et absences. Les créneaux proposés à la prise de RDV en découlent.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {!editable && doctorId != null && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                <i className="bx bx-lock-alt text-sm" aria-hidden /> Lecture seule
              </span>
            )}
            <div className="w-64">
              <DoctorSelect
                label="Médecin"
                doctors={doctors}
                value={doctorId != null ? String(doctorId) : ""}
                placeholder={doctors.length === 0 ? "Aucun médecin" : undefined}
                onChange={(value) => setSearchParams(value ? { [AVAILABILITY_DOCTOR_PARAM]: value } : {})}
              />
            </div>
          </div>
        </div>

        {doctorId != null ? (
          <DoctorAvailabilityPanel key={doctorId} doctorId={doctorId} editable={editable} />
        ) : (
          <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/40 px-6 py-12 text-center text-sm text-slate-500">
            Aucun médecin à afficher.
          </p>
        )}
      </div>
    </DashboardWrapper>
  );
};

export default Availability;
